// ============================================================
// ContactMarks - where the party last saw what it cannot see, on the display
// (plan map-display, H; the rulings: suspected = "?" + trail + last seen).
//
// One "?" for each contact the GM has put on the tracker, at its last
// sighting, with the sightings before it as a fading dotted trail. Read off
// the published Stealth frame (engine/stealth/contacts), which names no
// figure: these marks are all a player's screen knows. Drawn under the
// figures; they take no pointer.
// ============================================================
import { memo } from 'react';
import { projectGround, type Camera } from '../../engine/battlefield/projection';
import type { PublicContacts } from '../../engine/stealth/contacts';
import styles from '../../styles/Display.module.css';

function ContactMarks({ contacts, camera }: { contacts: PublicContacts['contacts'] | undefined; camera: Camera }) {
  if (!contacts?.length) return null;
  return (
    <g data-testid="contact-marks" pointerEvents="none">
      {contacts.map((c) => {
        const at = projectGround(c.lastSeen, camera);
        if (at.behind) return null;
        return (
          <g key={c.cid} className={styles.contactMark} data-contact={c.cid}>
            {c.trail.map((p, i) => {
              const t = projectGround(p, camera);
              // Older sightings fainter: the trail fades towards where it came from.
              return t.behind ? null : (
                <circle key={i} cx={t.x} cy={t.y} r={2.5} opacity={(i + 1) / (c.trail.length + 1)} />
              );
            })}
            <circle cx={at.x} cy={at.y} r={10} className={styles.contactRing} />
            <text x={at.x} y={at.y} className={styles.contactGlyph} dominantBaseline="central" textAnchor="middle">
              ?
            </text>
          </g>
        );
      })}
    </g>
  );
}

export default memo(ContactMarks);
